import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type PublishMockupType =
  | 'flat'
  | 'model'
  | 'lifestyle'
  | 'hanger'
  | 'folded'
  | 'closeup';

/**
 * `skipped`: the auto-start was suppressed because the loaded design already
 * carries a saved title (see hydrateFromDesign). Like `success` it keeps
 * useAiProductDescription quiet, but no suggestion was applied, so the
 * Publish step shows no "applied" status. "Neu vorschlagen" still works.
 */
export type AiGenerationStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'error'
  | 'skipped';

/** The saved fields a reopened design puts back into the Publish step. */
export interface PublishHydration {
  title: string;
  description: string;
  tags: string[];
  /** True when a real title came back: the AI auto-start must not replace it. */
  suppressAutoSuggest: boolean;
}

export interface AiGenerationResult {
  title: string;
  description: string;
  tags: string[];
}

/**
 * Mockup types offered in the Publish step. Label and description live in
 * the locale files under `creator.publish.mockupTypes.<id>`.
 */
export const MOCKUP_TYPES: Array<{ id: PublishMockupType }> = [
  { id: 'flat' },
  { id: 'model' },
  { id: 'lifestyle' },
  { id: 'hanger' },
  { id: 'folded' },
  { id: 'closeup' },
];

interface PublishState {
  title: string;
  description: string;
  tags: string[];
  tagInput: string;
  category: string;
  collection: string;
  mockupTypes: PublishMockupType[];
  /**
   * Channels the seller picked in the publish step. Empty until the channel
   * resolution answers: the destinations come from the server, not from a
   * hard-coded list.
   */
  selectedChannelIds: string[];
  aiGenerationStatus: AiGenerationStatus;
  aiGenerationError: string | null;
  /**
   * Monotonic id of the latest AI suggestion request. A response only lands
   * when it carries the current id; reset, a design switch and the start of
   * publishing bump it so a late answer cannot write into the wrong design.
   */
  aiRequestId: number;
  /**
   * True from the publish click until the handover to the marketplace or
   * the cloud is done or the publish aborted. While set, the auto-save
   * timer stays quiet, the step bar is locked and leaving the creator is
   * refused, so no save can slip in after the status turned ACTIVE. Owned
   * by usePublishDesign alone; `reset` leaves it untouched.
   */
  isPublishing: boolean;

  setTitle: (value: string) => void;
  setDescription: (value: string) => void;
  /**
   * Put a loaded design's saved title, description and tags back in one
   * update (CreatorPage on a genuine design switch, after `reset`). Sets the
   * AI status to `skipped` when a title came back, so the auto-start on the
   * products step leaves the shop's title alone.
   */
  hydrateFromDesign: (fields: PublishHydration) => void;
  setTagInput: (value: string) => void;
  addTag: (value: string) => void;
  removeTag: (value: string) => void;
  setCategory: (value: string) => void;
  setCollection: (value: string) => void;
  toggleMockupType: (value: PublishMockupType) => void;
  toggleChannel: (channelId: string) => void;
  setChannels: (channelIds: string[]) => void;
  /** Start a suggestion run; returns the request id the caller hands back. */
  beginAiGeneration: () => number;
  /** Replace title, description and tags — ignored when the id is stale. */
  applyAiGenerationResult: (
    requestId: number,
    result: AiGenerationResult
  ) => void;
  /** Mark the run as failed, keeping the fields — ignored when the id is stale. */
  failAiGeneration: (requestId: number, message: string) => void;
  /** Invalidate any in-flight run without touching status or fields. */
  invalidateAiGeneration: () => void;
  setIsPublishing: (value: boolean) => void;
  reset: () => void;
}

const initialPublishState = {
  title: '',
  description: '',
  tags: [] as string[],
  tagInput: '',
  category: '',
  collection: '',
  mockupTypes: ['flat', 'model', 'lifestyle'] as PublishMockupType[],
  selectedChannelIds: [] as string[],
  aiGenerationStatus: 'idle' as AiGenerationStatus,
  aiGenerationError: null as string | null,
};

/**
 * Publish store - UI state for Step 3.
 */
export const usePublishStore = create<PublishState>()(
  devtools(
    (set, get) => ({
      ...initialPublishState,
      aiRequestId: 0,
      isPublishing: false,

      setTitle: (value) => set({ title: value }),
      setDescription: (value) => set({ description: value }),
      hydrateFromDesign: ({
        title,
        description,
        tags,
        suppressAutoSuggest,
      }) => {
        const cleanTags: string[] = [];
        for (const tag of tags) {
          const trimmed = tag.trim();
          if (trimmed && !cleanTags.includes(trimmed)) cleanTags.push(trimmed);
        }
        set({
          title,
          description,
          tags: cleanTags,
          tagInput: '',
          ...(suppressAutoSuggest
            ? {
                aiGenerationStatus: 'skipped' as const,
                aiGenerationError: null,
              }
            : {}),
        });
      },
      setTagInput: (value) => set({ tagInput: value }),
      addTag: (value) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        const current = get().tags;
        if (current.includes(trimmed)) return;
        set({ tags: [...current, trimmed], tagInput: '' });
      },
      removeTag: (value) =>
        set({ tags: get().tags.filter((tag) => tag !== value) }),
      setCategory: (value) => set({ category: value }),
      setCollection: (value) => set({ collection: value }),
      toggleMockupType: (value) => {
        const current = get().mockupTypes;
        const next = current.includes(value)
          ? current.filter((type) => type !== value)
          : [...current, value];
        set({ mockupTypes: next });
      },
      toggleChannel: (value) => {
        const current = get().selectedChannelIds;
        const next = current.includes(value)
          ? current.filter((channel) => channel !== value)
          : [...current, value];
        set({ selectedChannelIds: next });
      },
      setChannels: (value: string[]) => set({ selectedChannelIds: value }),
      beginAiGeneration: () => {
        const requestId = get().aiRequestId + 1;
        set({
          aiRequestId: requestId,
          aiGenerationStatus: 'loading',
          aiGenerationError: null,
        });
        return requestId;
      },
      applyAiGenerationResult: (requestId, result) => {
        if (requestId !== get().aiRequestId) return;
        set({
          title: result.title,
          description: result.description,
          tags: result.tags,
          tagInput: '',
          aiGenerationStatus: 'success',
          aiGenerationError: null,
        });
      },
      failAiGeneration: (requestId, message) => {
        if (requestId !== get().aiRequestId) return;
        set({
          aiGenerationStatus: 'error',
          aiGenerationError: message,
        });
      },
      invalidateAiGeneration: () => set({ aiRequestId: get().aiRequestId + 1 }),
      setIsPublishing: (value) => set({ isPublishing: value }),
      // The request id keeps counting across resets so a run started before
      // the reset can never match a run started after it.
      reset: () =>
        set({ ...initialPublishState, aiRequestId: get().aiRequestId + 1 }),
    }),
    { name: 'publish-store' }
  )
);
